import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Alert,
} from 'reactstrap';
import { OneShotSecretReveal } from '../../components/OneShotSecretReveal';
import { isApiError } from '../../api/errors';
import { useCreateWebhook } from './useWebhooks';

/**
 * Zwei-Stufen-Flow:
 *   1. Form (Label, URL, Event-Filter-Checkboxes)
 *   2. OneShotSecretReveal mit HMAC-Signing-Secret
 *
 * Spec: lmm-wiki/wiki/orbit-specs/outbound-webhooks-admin.md
 */
export function CreateWebhookModal({ isOpen, onClose, knownEventTypes }) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('https://');
  const [filters, setFilters] = useState([]);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const create = useCreateWebhook();

  const reset = () => {
    setLabel('');
    setUrl('https://');
    setFilters([]);
    setError(null);
    setCreated(null);
  };

  const finishAndClose = () => {
    reset();
    onClose();
  };

  const toggle = (e) => {
    setFilters((prev) =>
      prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e],
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!label.trim()) {
      setError('Label ist erforderlich.');
      return;
    }
    if (!/^https:\/\//i.test(url)) {
      setError('URL muss mit https:// beginnen.');
      return;
    }
    if (filters.length === 0) {
      setError('Mindestens ein Event-Filter erforderlich.');
      return;
    }
    try {
      const res = await create.mutateAsync({
        label: label.trim(),
        url: url.trim(),
        eventFilters: filters,
      });
      setCreated(res);
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Anlegen fehlgeschlagen.');
    }
  };

  if (created) {
    return (
      <OneShotSecretReveal
        isOpen
        label="Webhook-Signing-Secret"
        secret={created.secret}
        confirmPhrase="Empfänger wurde mit dem HMAC-Secret konfiguriert"
        onAcknowledged={finishAndClose}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={create.isPending ? undefined : finishAndClose} size="lg" backdrop="static">
      <ModalHeader toggle={create.isPending ? undefined : finishAndClose}>
        Neuer Outbound-Webhook
      </ModalHeader>
      <form onSubmit={onSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="hookLabel">Label</Label>
            <Input
              id="hookLabel"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z. B. valiKey backend, Slack-Bridge…"
              required
              maxLength={100}
            />
          </FormGroup>
          <FormGroup>
            <Label for="hookUrl">URL</Label>
            <Input
              id="hookUrl"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            <small className="text-muted">
              Server lehnt <code>localhost</code>, RFC1918-IPs und Non-HTTPS via SSRF-Guard ab.
            </small>
          </FormGroup>
          <FormGroup>
            <Label>Event-Filter</Label>
            <div className="mb-2">
              <FormGroup check inline>
                <Input
                  type="checkbox"
                  id="filter-wildcard"
                  checked={filters.includes('*')}
                  onChange={() => toggle('*')}
                />
                <Label check for="filter-wildcard" style={{ fontFamily: 'monospace' }}>
                  <code>*</code> · alle Events
                </Label>
              </FormGroup>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {(knownEventTypes ?? []).map((e) => (
                <FormGroup check inline key={e}>
                  <Input
                    type="checkbox"
                    id={`filter-${e}`}
                    checked={filters.includes(e)}
                    onChange={() => toggle(e)}
                  />
                  <Label
                    check
                    for={`filter-${e}`}
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  >
                    {e}
                  </Label>
                </FormGroup>
              ))}
            </div>
            <small className="text-muted">
              Pattern wie <code>user.*</code> matched alle <code>user.</code>-Events.
            </small>
          </FormGroup>
          {error && <Alert color="danger">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={finishAndClose} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button color="primary" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Anlegen…' : 'Anlegen'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
